import { useNavigation } from '@react-navigation/native';
import { ResponsiveValue } from '@shopify/restyle';
import * as Clipboard from 'expo-clipboard';
import React from 'react';
import { Platform, TextProps } from 'react-native';
import {
  Config,
  isTriggerConfig,
  parseValue,
  Part,
} from 'react-native-controlled-mentions';

import Highlighter from '@components/HighlightWords';
import { useAlert } from '@hooks/useAlert';
import { useChatMentionPress } from '@hooks/useChatMentionPress';
import { useAppNavigation } from '@navigation/useAppNavigation';
import {
  copyLinkUrl,
  openLinkUrl,
  splitTextWithLinks,
} from '@utils/linkifyText';

import { EVERYONE_ID } from './MentionSuggestions';
import theme, { Theme } from '../../../themes/theme';
import { User } from '../../graphql/generated';
import { Text } from '../Restyle';

const wrapStyle = {
  borderWidth: 0 as const,
  borderColor: 'transparent' as const,
  ...Platform.select({ web: { wordBreak: 'break-word' as const } }),
};

type MessagePartsType = {
  value: string;
  needBoldString?: string[];
  configs: Config[];
  styleLinks?: boolean;
  filterVal?: string;
  textColor?: ResponsiveValue<keyof Theme['colors'], Theme['breakpoints']>;
  textVariant?: React.ComponentProps<typeof Text>['variant'];

  // if rendering mention from a non-chat detail screen
  navigateToChatOnMentionPress?: boolean;
  disableLongPress?: boolean;
  linkUserInMessage?: User;
  externalNavigate?: (url: string) => void;
} & TextProps;

const MessageParts: React.FC<MessagePartsType> = ({
  value,
  needBoldString,
  configs,
  styleLinks,
  filterVal = '',
  textColor = 'textSecondary',
  textVariant,
  navigateToChatOnMentionPress,
  disableLongPress = true,
  linkUserInMessage,
  externalNavigate,

  ...rest
}) => {
  const { parts } = parseValue(value, configs);
  const navigate = useAppNavigation();
  const { showAlert } = useAlert();
  const navigation = useNavigation();
  const resolvedTextVariant =
    textVariant ?? (Platform.OS === 'web' ? 'webBodySecondary' : 'body1');

  const { onMentionPress } = useChatMentionPress(navigateToChatOnMentionPress);

  const highlightText = (body: string) => {
    if (needBoldString) {
      return (
        <Highlighter
          autoEscape
          highlightStyle={{
            fontFamily: 'Inter_600SemiBold',
            fontWeight: '700',
            backgroundColor: 'white',
          }}
          searchWords={needBoldString}
          textToHighlight={body}
        />
      );
    }
    if (!filterVal) {
      return body;
    }
    return (
      <Highlighter
        autoEscape
        highlightStyle={{
          backgroundColor: theme.colors.yellowBright,
        }}
        searchWords={filterVal.split(' ')}
        textToHighlight={body}
      />
    );
  };

  const LinkUserText = (body: string, index: number) => {
    const username = '@' + (linkUserInMessage?.name ?? 'someone');
    let parts = body.split(username);
    if (parts.length === 2) {
      parts = [parts[0], username, parts[1]];
    }
    return (
      <Text
        key={index}
        variant={resolvedTextVariant}
        color={textColor}
        style={wrapStyle}>
        {parts.map((part, index1) => {
          const innerKey = index.toString() + index1.toString();
          if (index1 === 1) {
            return (
              <Text
                key={innerKey}
                variant={resolvedTextVariant}
                color='linkColor'
                onPress={() => {
                  if (linkUserInMessage)
                    if (Platform.OS === 'web') {
                      if (externalNavigate) {
                        externalNavigate.navigateToContact(linkUserInMessage);
                      } else {
                        navigate.navigateToContact(linkUserInMessage);
                      }
                    } else {
                      navigation.navigate('user-profile', {
                        userId: linkUserInMessage.id,
                      });
                    }
                }}>
                {part}
              </Text>
            );
          }
          return linkifyPlainTextPart(
            part,
            index1,
            <Text
              key={innerKey + '-highlight'}
              variant={resolvedTextVariant}
              color={textColor}>
              {highlightText(part)}
            </Text>
          );
        })}
      </Text>
    );
  };

  const copyToClipboard = (text: string) => {
    Clipboard.setStringAsync(text).then(() => {
      const copiedConfirm = `Copied to clipboard`;
      showAlert(copiedConfirm);
    });
  };

  const makePlainText = (
    content: string,
    segKey: string | number,
    style?: object
  ) => (
    <Text
      key={segKey}
      selectable={Platform.OS === 'web' || !disableLongPress}
      variant={resolvedTextVariant}
      color={textColor}
      onLongPress={
        disableLongPress ? undefined : () => copyToClipboard(content)
      }
      style={style}>
      {highlightText(content)}
    </Text>
  );

  // Splits a plain-text part into URL and non-URL <Text> segments instead of
  // wrapping in react-native-hyperlink. Hyperlink always renders a View
  // wrapper (TA-10489), which breaks multiline layout when placed alongside
  // mention parts inside the outer Text tree on native (TA-10697).
  const linkifyPlainTextPart = (
    text: string,
    index: number,
    fallbackNode: React.ReactElement
  ) => {
    if (!styleLinks) return fallbackNode;

    const segments = splitTextWithLinks(
      text,
      `${index}`,
      (segment, key) => makePlainText(segment, key),
      (url, displayText, key) => (
        <Text
          key={key}
          selectable={Platform.OS === 'web' || !disableLongPress}
          variant={resolvedTextVariant}
          style={{ color: theme.colors.blue }}
          onPress={() => openLinkUrl(url)}
          onLongPress={
            Platform.OS !== 'web'
              ? () => copyLinkUrl(url, showAlert)
              : undefined
          }>
          {highlightText(displayText)}
        </Text>
      )
    );

    if (!segments) return fallbackNode;

    return (
      <Text
        key={index}
        selectable={Platform.OS === 'web' || !disableLongPress}
        variant={resolvedTextVariant}
        color={textColor}
        style={wrapStyle}>
        {segments}
      </Text>
    );
  };

  const renderPart = (part: Part, index: number) => {
    // Mention type part
    if (part.config && isTriggerConfig(part.config)) {
      const { id: userId } = part.data || { userId: '' };
      const disablePress = !userId || userId === EVERYONE_ID;

      const onPress = () => {
        if (userId) onMentionPress(userId);
      };

      // `onLongPress` goes directly on this <Text> — RN Text natively
      // supports onPress/onLongPress. A <Pressable> wrapper would nest a View
      // inside the outer Text tree, which Android mismeasures alongside sibling
      // parts (TA-10314). `selectable` is web-forced true regardless of
      // `disableLongPress` (TA-10483): on web it gates browser drag-selection,
      // unrelated to the long-press-to-copy feature `disableLongPress` toggles.
      return (
        <Text
          key={index}
          selectable={Platform.OS === 'web' || !disableLongPress}
          style={!filterVal && part.config.textStyle}
          variant={resolvedTextVariant}
          color={textColor}
          onPress={disablePress ? undefined : onPress}
          onLongPress={
            disableLongPress ? undefined : () => copyToClipboard(part.text)
          }>
          {highlightText(part.text)}
        </Text>
      );
    }

    if (linkUserInMessage && disableLongPress)
      return LinkUserText(part.text, index);
    return linkifyPlainTextPart(
      part.text,
      index,
      makePlainText(part.text, index, wrapStyle)
    );
  };
  return (
    <Text paddingTop='xxxs' variant={resolvedTextVariant} {...rest}>
      {parts.map(renderPart)}
    </Text>
  );
};

export default MessageParts;
